import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid, page } from './common.js';
/** Module `permits`: permit to work (docs/modules/permits.md, spec 8.6.2). Owner: task S32. */
export const PermitSignOffRole = z.enum(['issuer', 'contractor', 'fire_watch', 'area_owner', 'isolator']);
export const PermitType = z.object({
    id: Uuid,
    name: z.string(),
    description: z.string(),
    checklist: z.array(z.object({ id: z.string().min(1).max(40), text: z.string(), required: z.boolean() })),
    precautions: z.string(),
    /** Who must sign to issue (issuer always) and to close (issuer and contractor always). */
    issueSignOffs: z.array(PermitSignOffRole),
    closeSignOffs: z.array(PermitSignOffRole),
    maxHours: z.number().int(),
    active: z.boolean(),
});
export const PermitTypeCreate = z.object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(1000).default(''),
    checklist: z.array(z.object({ id: z.string().min(1).max(40), text: z.string().trim().min(1).max(300), required: z.boolean().default(true) })).max(50).default([]),
    precautions: z.string().max(5000).default(''),
    issueSignOffs: z.array(PermitSignOffRole).default(['issuer']),
    closeSignOffs: z.array(PermitSignOffRole).default(['issuer', 'contractor']),
    maxHours: z.number().int().min(1).max(336).default(12),
});
/** Every field optional with no defaults (a PATCH must not reset what it does not mention). */
export const PermitTypeUpdate = z.object({
    name: PermitTypeCreate.shape.name.optional(),
    description: z.string().trim().max(1000).optional(),
    checklist: z.array(z.object({ id: z.string().min(1).max(40), text: z.string().trim().min(1).max(300), required: z.boolean().default(true) })).max(50).optional(),
    precautions: z.string().max(5000).optional(),
    issueSignOffs: z.array(PermitSignOffRole).optional(),
    closeSignOffs: z.array(PermitSignOffRole).optional(),
    maxHours: z.number().int().min(1).max(336).optional(),
    active: z.boolean().optional(),
});
export const PermitStatus = z.enum(['requested', 'issued', 'active', 'suspended', 'closed', 'cancelled', 'expired']);
export const PermitSignature = z.object({
    role: PermitSignOffRole,
    name: z.string(),
    signedAt: IsoDateTime,
    stage: z.enum(['issue', 'accept', 'close', 'suspend', 'resume']),
    /** The `permit_signature` file holding the drawn strokes (S32). */
    fileId: Uuid.optional(),
});
export const PermitEvent = z.object({
    id: Uuid,
    kind: z.enum(['raised', 'issued', 'accepted', 'suspended', 'resumed', 'closed', 'cancelled', 'expired', 'alert']),
    actorLabel: z.string(),
    at: IsoDateTime,
    note: z.string(),
});
export const Permit = z.object({
    id: Uuid,
    number: z.string(),
    typeId: Uuid,
    typeName: z.string(),
    siteId: Uuid,
    status: PermitStatus,
    memberId: Uuid.nullable(),
    visitId: Uuid.nullable(),
    preRegistrationId: Uuid.nullable(),
    holderName: z.string(),
    holderCompany: z.string(),
    area: z.string(),
    description: z.string(),
    conditions: z.string(),
    startsAt: IsoDateTime.nullable(),
    endsAt: IsoDateTime.nullable(),
    checklist: z.array(z.object({ id: z.string(), text: z.string(), checked: z.boolean() })),
    signatures: z.array(PermitSignature),
    requestedByLabel: z.string(),
    issuedByLabel: z.string().nullable(),
    suspendedReason: z.string().nullable(),
    pdfFileId: Uuid.nullable(),
    /** SHA-256 of the latest PDF (S32). */
    pdfSha256: z.string().nullable().optional(),
    closedAt: IsoDateTime.nullable().optional(),
    /** Only on GET /permits/:id. */
    events: z.array(PermitEvent).optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** Raise (reception, a manager, or the contractor through the app / pre-arrival page). */
export const PermitRaise = z.object({
    typeId: Uuid,
    siteId: Uuid,
    visitId: Uuid.nullable().default(null),
    preRegistrationId: Uuid.nullable().default(null),
    memberId: Uuid.nullable().default(null),
    holderName: z.string().trim().max(120).default(''),
    holderCompany: z.string().trim().max(120).default(''),
    area: z.string().trim().max(200).default(''),
    description: z.string().trim().max(2000).default(''),
});
/** Signature strokes JSON (ADR 0010 format). */
const Strokes = z.unknown();
export const PermitIssue = z.object({
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    area: z.string().trim().min(1).max(200),
    conditions: z.string().trim().max(5000).default(''),
    checklist: z.record(z.string(), z.boolean()),
    signatures: z.array(z.object({ role: PermitSignOffRole, name: z.string().trim().min(1).max(120), strokes: Strokes })).min(1),
});
/**
 * `stage` 'accept' (default) is the holder accepting an issued permit. 'close' is the holder's close-out signature on an active
 * permit (given on the app or pre-arrival page), so the issuer can close it from the portal without the contractor present.
 */
export const PermitAccept = z.object({
    name: z.string().trim().min(1).max(120),
    strokes: Strokes,
    occurredAt: IsoDateTime.optional(),
    stage: z.enum(['accept', 'close']).default('accept'),
});
export const PermitSuspend = z.object({ reason: z.string().trim().min(1).max(500) });
export const PermitClose = z.object({
    signatures: z.array(z.object({ role: PermitSignOffRole, name: z.string().trim().min(1).max(120), strokes: Strokes })).min(1),
    notes: z.string().trim().max(2000).default(''),
});
export const PermitListQuery = PageQuery.extend({ siteId: Uuid.optional(), status: z.enum(['open', 'closed', 'all']).default('open'), typeId: Uuid.optional() });
export const PermitList = page(Permit);
/** Kiosk step `permits` config: nothing offline; the kiosk fetches GET /permits/device/for-sign-in when online. */
export const KioskPermitsForSignIn = z.object({ permits: z.array(Permit.pick({ id: true, number: true, typeName: true, area: true, conditions: true, startsAt: true, endsAt: true }).extend({ precautions: z.string() })) });
/** Kiosk step `permits` input (event.steps.permits). */
export const PermitsStepInput = z.object({ accepted: z.array(z.object({ permitId: Uuid, name: z.string().max(120), strokes: Strokes })) });
/** Webhook payload for permit.issued / permit.closed (no names: ids, number, type and times only). */
export const PublicPermit = z.object({
    id: Uuid,
    number: z.string(),
    typeName: z.string(),
    siteId: Uuid,
    visitId: Uuid.nullable(),
    memberId: Uuid.nullable(),
    status: PermitStatus,
    startsAt: IsoDateTime.nullable(),
    endsAt: IsoDateTime.nullable(),
    closedAt: IsoDateTime.nullable(),
});
/** The pre-arrival page's permits for one pre-registration (GET /permits/prearrival/:token). */
export const PrearrivalPermits = z.object({
    permits: z.array(Permit.pick({ id: true, number: true, typeName: true, status: true, area: true, conditions: true, startsAt: true, endsAt: true }).extend({ precautions: z.string(), accepted: z.boolean() })),
});
export const PermitsTenantSettings = z.object({
    /** Permits not tied to a visit are kept this many days after closing; 0 = keep (ADR 0025). */
    retentionDays: z.number().int().min(0).default(1095),
    /** Alert the issuer and reception when the holder signs out with a permit still open. */
    alertOpenAtSignOut: z.boolean().default(true),
    /** Alert when an issued or active permit passes its end time without closing. */
    alertOnExpiry: z.boolean().default(true),
    numberPrefix: z.string().max(10).default('PTW'),
});
//# sourceMappingURL=permits.js.map